import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ProfileCodeChip } from "@/components/ProfileCodeChip";
import { Loader } from "@/components/EmptyState";
import { Clock, XCircle, LogOut, RefreshCw } from "lucide-react";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";
import { needsPlan } from "@/lib/subscription";
import { PlanPicker } from "@/components/PlanPicker";

const FIELD_LABELS = {
  name: "Business Name", business_email: "Business Email", logo_file_id: "Business Logo",
  owner_name: "Owner Name", address: "Business Address", license_type: "License Type",
  license_number: "License Number", license_issue_date: "License Issue Date",
  license_expiry_date: "License Expiry Date", license_doc_file_id: "License Document",
};

export default function Gate() {
  const navigate = useNavigate();
  const { user, business, loading, logout, refresh } = useAuth();
  const [refreshing, setRefreshing] = useState(false);

  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  const reg = business?.registration_status;
  const active = reg === "approved" && business?.account_active;
  if (active && !needsPlan(business)) return <Navigate to="/app" replace />;
  if (active) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] rv-grain">
        <div className="mx-auto flex max-w-7xl justify-end px-6 pt-6">
          <Button variant="outline" size="sm" data-testid="plan-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>Sign out</Button>
        </div>
        <PlanPicker />
      </div>
    );
  }

  const doRefresh = async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
    toast.success("Status refreshed");
  };

  const flagged = business?.rejected_fields || [];

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] rv-grain p-6">
      <div className="w-full max-w-lg rounded-3xl border border-[#E5E0D8] bg-white p-8 shadow-sm" data-testid="gate-card">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-extrabold text-stone-900">{business?.name}</h1>
          <ProfileCodeChip code={business?.profile_code} />
        </div>
        <p className="mt-1 text-sm capitalize text-stone-500">{BUSINESS_TYPE_LABELS[business?.business_type] || business?.business_type} account</p>

        {/* PENDING */}
        {reg === "pending" && (
          <div className="mt-8 text-center" data-testid="gate-pending">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50">
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="mt-4 font-heading text-xl font-bold text-stone-900">Verification In Progress</h2>
            <p className="mt-2 text-sm text-stone-500">
              Your profile has been submitted and is under review by our team. You'll be notified once a decision is made. You can safely close the app and sign back in anytime.
            </p>
            <Button data-testid="gate-refresh-btn" onClick={doRefresh} disabled={refreshing} className="mt-6 bg-[#0D5C3A] hover:bg-[#084229]">
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh Status
            </Button>
          </div>
        )}

        {/* APPROVED BUT DEACTIVATED */}
        {reg === "approved" && !business?.account_active && (
          <div className="mt-8 text-center" data-testid="gate-deactivated">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50">
              <XCircle className="h-8 w-8 text-rose-600" />
            </div>
            <h2 className="mt-4 font-heading text-xl font-bold text-stone-900">Account Deactivated</h2>
            <p className="mt-2 text-sm text-stone-500">
              Your account is currently deactivated. Please contact the platform administrator to restore access.
            </p>
            <Button data-testid="gate-refresh-btn" onClick={doRefresh} disabled={refreshing} className="mt-6 bg-[#0D5C3A] hover:bg-[#084229]">
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh Status
            </Button>
          </div>
        )}

        {/* REJECTED */}
        {reg === "rejected" && (
          <div className="mt-8" data-testid="gate-rejected">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rose-50">
                <XCircle className="h-6 w-6 text-rose-600" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-stone-900">Changes Required</h2>
                <p className="text-sm text-stone-500">An admin reviewed your profile and requested corrections.</p>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="gate-rejection-reason">
              <b>Reason:</b> {business?.rejection_reason || "Please review and correct your details."}
            </div>
            {flagged.length > 0 && (
              <div className="mt-3 rounded-xl border border-[#E5E0D8] px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Fields to correct</p>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {flagged.map((f) => (
                    <li key={f} className="rounded-full bg-[#F0ECE1] px-3 py-1 text-xs font-medium text-stone-700" data-testid={`gate-flagged-${f}`}>
                      {FIELD_LABELS[f] || f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Button data-testid="gate-resubmit-btn" onClick={() => navigate("/resubmit")} className="mt-6 w-full bg-[#C85A32] hover:bg-[#a8481f]">
              Edit & Resubmit
            </Button>
          </div>
        )}

        <Button variant="outline" data-testid="gate-logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="mt-4 w-full">
          <LogOut className="mr-2 h-4 w-4" /> Sign Out
        </Button>
      </div>
    </div>
  );
}
